import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  DatePicker,
  message,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  ExportOutlined,
} from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface LiveRoomUnpaidLog {
  id: number;
  liveRoomId: string;
  subGameName: string;
  subGameCode: string;
  anchorName: string;
  userId: string;
  username: string;
  balanceAtTrigger: number;
  dailyRechargeAmount: number;
  triggerTime: string;
}

const LiveRoomUnpaidLogs: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterLiveRoomId, setFilterLiveRoomId] = useState('');
  const [filterSubGame, setFilterSubGame] = useState('all');
  const [filterAnchor, setFilterAnchor] = useState('');
  const [dateRange, setDateRange] = useState<any>(null);

  // 模拟数据
  const mockData: LiveRoomUnpaidLog[] = [
    {
      id: 1,
      liveRoomId: 'LIVE_001',
      subGameName: '百家乐',
      subGameCode: 'BACCARAT_001',
      anchorName: '主播小美',
      userId: 'user_001',
      username: '张三',
      balanceAtTrigger: 50.00,
      dailyRechargeAmount: 0.00,
      triggerTime: '2025-10-23 10:30:00',
    },
    {
      id: 2,
      liveRoomId: 'LIVE_001',
      subGameName: '百家乐',
      subGameCode: 'BACCARAT_001',
      anchorName: '主播小美',
      userId: 'user_002',
      username: '李四',
      balanceAtTrigger: 30.00,
      dailyRechargeAmount: 0.00,
      triggerTime: '2025-10-23 11:15:00',
    },
    {
      id: 3,
      liveRoomId: 'LIVE_002',
      subGameName: '轮盘',
      subGameCode: 'ROULETTE_001',
      anchorName: '主播小帅',
      userId: 'user_003',
      username: '王五',
      balanceAtTrigger: 100.00,
      dailyRechargeAmount: 200.00,
      triggerTime: '2025-10-23 14:20:00',
    },
    {
      id: 4,
      liveRoomId: 'LIVE_003',
      subGameName: '龙虎',
      subGameCode: 'DRAGON_TIGER_001',
      anchorName: '主播小丽',
      userId: 'user_004',
      username: '赵六',
      balanceAtTrigger: 25.00,
      dailyRechargeAmount: 0.00,
      triggerTime: '2025-10-23 16:45:00',
    },
    {
      id: 5,
      liveRoomId: 'LIVE_001',
      subGameName: '百家乐',
      subGameCode: 'BACCARAT_001',
      anchorName: '主播小美',
      userId: 'user_005',
      username: '钱七',
      balanceAtTrigger: 80.00,
      dailyRechargeAmount: 100.00,
      triggerTime: '2025-10-23 18:30:00',
    },
    {
      id: 6,
      liveRoomId: 'LIVE_002',
      subGameName: '轮盘',
      subGameCode: 'ROULETTE_001',
      anchorName: '主播小帅',
      userId: 'user_001',
      username: '张三',
      balanceAtTrigger: 45.00,
      dailyRechargeAmount: 0.00,
      triggerTime: '2025-10-24 09:15:00',
    },
  ];

  const handleRefresh = () => {
    setLoading(true);
    message.loading('正在刷新数据...', 1);
    setTimeout(() => {
      setLoading(false);
      message.success('数据刷新成功！');
    }, 1500);
  };

  const handleExport = () => {
    message.loading('正在导出数据...', 1);
    setTimeout(() => {
      message.success('数据导出成功！');
    }, 1500);
  };

  const columns = [
    {
      title: '直播间ID',
      dataIndex: 'liveRoomId',
      key: 'liveRoomId',
      width: 120,
      fixed: 'left' as const,
    },
    {
      title: '关联子游戏',
      key: 'subGame',
      width: 150,
      render: (_: any, record: LiveRoomUnpaidLog) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.subGameName}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.subGameCode}</div>
        </div>
      ),
    },
    {
      title: '直播间主播名称',
      dataIndex: 'anchorName',
      key: 'anchorName',
      width: 120,
    },
    {
      title: '触发用户ID',
      key: 'userInfo',
      width: 120,
      render: (_: any, record: LiveRoomUnpaidLog) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.username}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.userId}</div>
        </div>
      ),
    },
    {
      title: '触发时余额',
      dataIndex: 'balanceAtTrigger',
      key: 'balanceAtTrigger',
      width: 120,
      sorter: (a: LiveRoomUnpaidLog, b: LiveRoomUnpaidLog) => a.balanceAtTrigger - b.balanceAtTrigger,
      render: (amount: number) => (
        <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
          ¥{amount.toFixed(2)}
        </span>
      ),
    },
    {
      title: '当日充值金额',
      dataIndex: 'dailyRechargeAmount',
      key: 'dailyRechargeAmount',
      width: 130,
      sorter: (a: LiveRoomUnpaidLog, b: LiveRoomUnpaidLog) => a.dailyRechargeAmount - b.dailyRechargeAmount,
      render: (amount: number) => (
        <span style={{ color: amount > 0 ? '#3f8600' : '#999' }}>
          ¥{amount.toFixed(2)}
        </span>
      ),
    },
    {
      title: '触发时间',
      dataIndex: 'triggerTime',
      key: 'triggerTime',
      width: 160,
      sorter: (a: LiveRoomUnpaidLog, b: LiveRoomUnpaidLog) => 
        new Date(a.triggerTime).getTime() - new Date(b.triggerTime).getTime(),
    },
  ];

  // 获取所有子游戏列表（用于筛选）
  const subGameList = Array.from(new Set(mockData.map(item => item.subGameName)));

  const filteredData = mockData.filter(item => {
    const matchesSearch = !searchText || 
      item.liveRoomId.toLowerCase().includes(searchText.toLowerCase()) ||
      item.userId.toLowerCase().includes(searchText.toLowerCase()) ||
      item.username.toLowerCase().includes(searchText.toLowerCase()) ||
      item.anchorName.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesLiveRoomId = !filterLiveRoomId || item.liveRoomId === filterLiveRoomId;
    const matchesSubGame = filterSubGame === 'all' || item.subGameName === filterSubGame;
    const matchesAnchor = !filterAnchor || item.anchorName.includes(filterAnchor);
    
    // 日期范围过滤
    const matchesDate = !dateRange || (
      new Date(item.triggerTime) >= dateRange[0].toDate() &&
      new Date(item.triggerTime) <= dateRange[1].toDate()
    );
    
    return matchesSearch && matchesLiveRoomId && matchesSubGame && matchesAnchor && matchesDate;
  });

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="直播未付费日志"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              导出Excel
            </Button>
          </Space>
        }
      >
        {/* 搜索和筛选 */}
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              placeholder="搜索直播间ID、用户ID、用户名或主播名称"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
            />
            <Input
              placeholder="直播间ID"
              value={filterLiveRoomId}
              onChange={(e) => setFilterLiveRoomId(e.target.value)}
              style={{ width: 150 }}
              allowClear
            />
            <Select
              value={filterSubGame}
              onChange={setFilterSubGame}
              style={{ width: 150 }}
            >
              <Option value="all">全部子游戏</Option>
              {subGameList.map(game => (
                <Option key={game} value={game}>{game}</Option>
              ))}
            </Select>
            <Input
              placeholder="主播名称"
              value={filterAnchor}
              onChange={(e) => setFilterAnchor(e.target.value)}
              style={{ width: 150 }}
              allowClear
            />
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              showTime
              style={{ width: 350 }}
              format="YYYY-MM-DD HH:mm:ss"
            />
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  );
};

export default LiveRoomUnpaidLogs;

